import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { aplicarPagina, lerLista, lerPagina } from '@titularis/ui';
import type { ExamModule, QuestionStatus } from '@titularis/api-client';
import { api } from '../../api';
import { MODULOS, STATUS_QUESTAO } from '../../dominio';
import { moduloDoFiltro, type FiltrosDeQuestoes } from './PainelDeFiltros';

/**
 * A consulta que define a lista de questões: o que filtra, em que ordem e que
 * página.
 *
 * Mora aqui, e não dentro da tela da lista, porque duas telas dependem dela: a
 * lista mostra a página, e a questão aberta caminha por ela com "Anterior" e
 * "Próxima". Lidas em dois lugares, as duas leituras divergiriam no primeiro
 * filtro novo - e a divergência apareceria como o "Próxima" pulando uma questão
 * que a lista mostrava, sem erro em lugar nenhum.
 */

export const TAMANHOS_DE_PAGINA = [20, 50, 100];

/*
 * Cem por página é o padrão porque a tela é de conferência em lote: o
 * administrador varre a prova inteira procurando o que falta, e vinte por página
 * transformavam uma prova de cem questões em cinco idas e voltas na paginação.
 */
export const POR_PAGINA_PADRAO = 100;

/**
 * As ordens da lista, com o mesmo eixo nos dois sentidos: "há mais tempo" é o
 * avesso de "recentemente", e é o que responde "o que ficou parado" - a pergunta
 * que a ordem por recentes nunca responde, porque o esquecido é justamente o que
 * ela empurra para a última página.
 */
export const ORDENS = [
  { value: 'prova', label: 'Ordem da prova' },
  { value: 'recentes', label: 'Editadas recentemente' },
  { value: 'antigas', label: 'Editadas há mais tempo' },
];

export type OrdemDaLista = 'recentes' | 'antigas';

export interface ConsultaDeQuestoes {
  aplicados: FiltrosDeQuestoes;
  termo: string;
  emRevisao: boolean;
  ordem?: OrdemDaLista;
  page: number;
  porPagina: number;
}

/**
 * Lê da URL a consulta inteira.
 *
 * O que vem de lá é texto que qualquer um pode digitar na barra de endereço,
 * então módulo, situação, ordem e tamanho de página passam pela lista de valores
 * conhecidos antes de virar consulta - o servidor recusaria, mas com erro em vez
 * de lista vazia.
 */
export function lerConsulta(params: URLSearchParams): ConsultaDeQuestoes {
  const texto = (chave: string): string | undefined => params.get(chave) ?? undefined;
  const ordem = texto('ordem');
  const porPagina = Number(texto('porPagina'));

  return {
    aplicados: {
      examId: texto('examId'),
      modules: lerLista(params, 'modules').filter((valor): valor is ExamModule =>
        MODULOS.includes(valor as ExamModule),
      ),
      statuses: lerLista(params, 'statuses').filter((valor): valor is QuestionStatus =>
        STATUS_QUESTAO.includes(valor as QuestionStatus),
      ),
      disciplineId: texto('disciplineId'),
      assuntos: {
        subjectIds: lerLista(params, 'subjects'),
        subjectDetailIds: lerLista(params, 'details'),
      },
      responsavel: lerLista(params, 'responsavel'),
      /*
       * "Fechada" é a régua do comentado (G-PROGRESS), e não um critério próprio
       * desta tela: objetiva com todos os itens comentados e ao menos uma aba;
       * subjetiva com Modelo de Resposta e ao menos uma aba.
       */
      fechadas: texto('fechadas'),
    },
    termo: texto('termo') ?? '',
    /*
     * Fila de revisão da máquina: as questões cuja última versão ainda é
     * sugestão, venha ela do Cadastro ou do Comentário Assistido. Vem da URL
     * porque a tela da prova manda o administrador para cá assim que o run
     * termina.
     */
    emRevisao: texto('revisao') === '1',
    ordem: ordem === 'recentes' || ordem === 'antigas' ? ordem : undefined,
    page: lerPagina(params),
    porPagina: TAMANHOS_DE_PAGINA.includes(porPagina) ? porPagina : POR_PAGINA_PADRAO,
  };
}

/**
 * Chave e função de uma página da lista.
 *
 * As duas telas pedem por aqui, então pedem exatamente a mesma coisa: a questão
 * aberta reaproveita, sem ir à rede, a página que a lista já carregou.
 */
export function paginaDaLista(consulta: ConsultaDeQuestoes, page = consulta.page) {
  const { aplicados, termo, emRevisao, ordem, porPagina } = consulta;

  /*
   * Responsável é um filtro só na tela, com pessoas e os dois módulos da máquina
   * lado a lado, mas três parâmetros na API: a lista de ids, a ausência de
   * responsável humano e qual módulo escreveu. Somam-se, não se cruzam.
   *
   * Os módulos vão **explícitos mesmo quando são os dois**: o servidor trataria
   * a lista cheia como a lista vazia, mas o que a tela pede é o que a URL diz, e
   * uma consulta que muda de forma conforme a quantidade de opções marcadas é
   * uma consulta a menos que alguém consegue ler.
   */
  const modulos = aplicados.responsavel
    .map((valor) => moduloDoFiltro(valor))
    .filter((modulo) => modulo !== undefined);
  const responsibleIds = aplicados.responsavel.filter((valor) => !moduloDoFiltro(valor));

  return {
    queryKey: ['questions', { aplicados, termo, emRevisao, ordem, page, porPagina }],
    queryFn: () =>
      api.listQuestions({
        examId: aplicados.examId,
        modules: aplicados.modules,
        statuses: aplicados.statuses,
        versionState: emRevisao ? 'suggestion' : undefined,
        disciplineId: aplicados.disciplineId,
        subjectIds: aplicados.assuntos.subjectIds,
        subjectDetailIds: aplicados.assuntos.subjectDetailIds,
        responsibleIds,
        fromIngestion: modulos.length > 0 ? 'true' : undefined,
        ingestionKinds: modulos,
        comentada: aplicados.fechadas,
        termo: termo || undefined,
        ordem,
        page,
        pageSize: porPagina,
      }),
  };
}

/** Uma vizinha e a origem que ela leva - que muda quando vira a página. */
export interface Vizinha {
  id: string;
  origem: string;
}

export interface Navegacao {
  /** Falso quando a questão não veio de uma lista: não há por onde caminhar. */
  temLista: boolean;
  carregando: boolean;
  anterior: Vizinha | null;
  proxima: Vizinha | null;
  /** Posição na lista inteira, contando as páginas anteriores. */
  posicao: number | null;
  total: number;
}

/** Onde a questão está na lista e quem são suas vizinhas. */
export function vizinhasNaLista({
  itens,
  questionId,
  origem,
  page,
  porPagina,
  total,
  ultimaDaAnterior,
  primeiraDaSeguinte,
}: {
  itens: { id: string }[];
  questionId: string | undefined;
  /** A query string da lista, que as vizinhas herdam - menos quem vira a página. */
  origem: string;
  page: number;
  porPagina: number;
  total: number;
  /** Só na borda: a última da página anterior e a primeira da seguinte. */
  ultimaDaAnterior?: { id: string };
  primeiraDaSeguinte?: { id: string };
}): { anterior: Vizinha | null; proxima: Vizinha | null; posicao: number | null } {
  const indice = itens.findIndex((item) => item.id === questionId);
  if (indice < 0) return { anterior: null, proxima: null, posicao: null };

  /*
   * Virar a página muda a origem junto: sem isso o "voltar" devolveria a página
   * errada, e o passo seguinte procuraria a questão numa página onde ela não
   * está - a navegação morreria na segunda virada.
   */
  const comPagina = (destino: number) =>
    aplicarPagina(new URLSearchParams(origem), destino).toString();

  let anterior: Vizinha | null = null;
  if (indice > 0) anterior = { id: itens[indice - 1]!.id, origem };
  else if (page > 1 && ultimaDaAnterior) {
    anterior = { id: ultimaDaAnterior.id, origem: comPagina(page - 1) };
  }

  let proxima: Vizinha | null = null;
  if (indice < itens.length - 1) proxima = { id: itens[indice + 1]!.id, origem };
  else if (page * porPagina < total && primeiraDaSeguinte) {
    proxima = { id: primeiraDaSeguinte.id, origem: comPagina(page + 1) };
  }

  return { anterior, proxima, posicao: (page - 1) * porPagina + indice + 1 };
}

/**
 * As vizinhas de uma questão na lista de onde ela foi aberta.
 *
 * A lista vem da `origem` - a query string que a tela da lista carimbou no
 * `state` da navegação -, e não dos filtros atuais de lugar nenhum: o que se
 * percorre é o conjunto que estava na tela quando a questão foi aberta, na ordem
 * em que estava.
 */
export function useVizinhas(questionId: string | undefined, origem: string | undefined): Navegacao {
  /*
   * `origem` vazia é uma lista legítima - a lista sem filtro nenhum -, então o
   * que distingue "não veio de lista" é a ausência, e não o texto vazio.
   */
  const temLista = origem !== undefined && Boolean(questionId);
  const consulta = lerConsulta(new URLSearchParams(origem ?? ''));
  const { page, porPagina } = consulta;

  const pagina = useQuery({ ...paginaDaLista(consulta), enabled: temLista });

  /*
   * A página congela quando a questão sai dela.
   *
   * Salvar invalida a listagem, e a questão recém-comentada some de um filtro
   * como "falta comentar" - que é justamente o filtro de quem está percorrendo a
   * fila. Recalcular sobre a lista nova tiraria o "Próxima" da tela no momento
   * exato em que ele seria usado. O conjunto a percorrer é o que estava na tela
   * quando a questão foi aberta, e a última página que continha a questão é
   * quem o guarda.
   */
  const [ultimaComAQuestao, setUltimaComAQuestao] = useState<typeof pagina.data>(undefined);
  const contem = Boolean(pagina.data?.items.some((item) => item.id === questionId));
  /*
   * Guardado durante a renderização, e não num efeito: o React refaz esta
   * renderização com o valor novo, sem passar por um quadro em que a lista já
   * mudou e a guardada ainda é a anterior. A comparação é por referência, que o
   * cache da consulta mantém estável enquanto os dados não mudam.
   */
  if (contem && pagina.data !== ultimaComAQuestao) setUltimaComAQuestao(pagina.data);

  const lista = contem ? pagina.data : ultimaComAQuestao;
  const itens = lista?.items ?? [];
  const total = lista?.total ?? 0;
  const indice = itens.findIndex((item) => item.id === questionId);

  /*
   * As páginas vizinhas só são buscadas na borda: no meio da página - que é o
   * caso comum, com cem por página - a lista carregada já responde tudo.
   */
  const anteriores = useQuery({
    ...paginaDaLista(consulta, page - 1),
    enabled: temLista && indice === 0 && page > 1,
  });
  const seguintes = useQuery({
    ...paginaDaLista(consulta, page + 1),
    enabled: temLista && indice >= 0 && indice === itens.length - 1 && page * porPagina < total,
  });

  const vizinhas = vizinhasNaLista({
    itens,
    questionId,
    origem: origem ?? '',
    page,
    porPagina,
    total,
    ultimaDaAnterior: anteriores.data?.items.at(-1),
    primeiraDaSeguinte: seguintes.data?.items[0],
  });

  return { temLista, carregando: pagina.isPending && temLista, total, ...vizinhas };
}
